import React from 'react';

import {motion} from 'framer-motion';
import {useIntl} from 'react-intl';

export const ProductShowcaseSection: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const intl = useIntl();

  return (
    <section className="relative w-full bg-white overflow-hidden pb-36">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Stryd 5.0 Card */}
          <motion.div
            className="relative h-[500px] lg:h-[550px] rounded-2xl overflow-hidden group cursor-pointer"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true, margin: '-100px'}}
            transition={{duration: 0.6}}
            whileHover={{scale: 1.02}}
          >
            {/* Gradient Background for transparent image - Holiday themed */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-amber-100 to-amber-200" />

            {/* Product Image */}
            <div className="absolute inset-0 flex items-center justify-center p-8 lg:p-12">
              <img
                src="/images/stryd5/Still 3A-2.png"
                alt="Stryd 5.0"
                className="w-full h-full object-contain max-w-full max-h-full"
              />
            </div>

            {/* Bottom Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-transparent" />

            {/* Content Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 z-10">
              <div className="flex items-end justify-between gap-4">
                {/* Left Side - Product Info */}
                <div className="flex flex-col">
                  <h3 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-1">
                    {intl.formatMessage({
                      id: 'home.productShowcaseSection.titleSingle',
                    })}
                  </h3>
                  <p className="text-lg lg:text-xl text-white/90">
                    {intl.formatMessage({
                      id: 'home.productShowcaseSection.supporterSingle',
                    })}
                  </p>
                </div>

                {/* Right Side - Buttons */}
                <div className="flex gap-3 flex-shrink-0">
                  <motion.a
                    href="/store"
                    className="px-5 py-2.5 bg-white text-black font-medium rounded-full text-sm lg:text-base hover:bg-gray-100 transition-all duration-300"
                    whileHover={{scale: 1.05}}
                    whileTap={{scale: 0.95}}
                  >
                    {intl.formatMessage({
                      id: 'home.productShowcaseSection.shopButton',
                    })}
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stryd Duo 5.0 Card */}
          <motion.div
            className="relative h-[500px] lg:h-[550px] rounded-2xl overflow-hidden group cursor-pointer"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true, margin: '-100px'}}
            transition={{duration: 0.6, delay: 0.2}}
            whileHover={{scale: 1.02}}
          >
            {/* Gradient Background for transparent image - Holiday themed */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-orange-300 to-orange-400" />

            {/* Product Image */}
            <div className="absolute inset-0 flex items-center justify-center p-8 lg:p-12">
              <img
                src="/images/stryd5/Still 2B.png"
                alt="Stryd Duo 5.0"
                className="w-full h-full object-contain max-w-full max-h-full"
              />
            </div>

            {/* Bottom Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-transparent" />

            {/* Content Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 z-10">
              <div className="flex items-end justify-between gap-4">
                {/* Left Side - Product Info */}
                <div className="flex flex-col">
                  <h3 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-1">
                    {intl.formatMessage({
                      id: 'home.productShowcaseSection.titleDuo',
                    })}
                  </h3>
                  <p className="text-lg lg:text-xl text-white/90">
                    {intl.formatMessage({
                      id: 'home.productShowcaseSection.supporterDuo',
                    })}
                  </p>
                </div>

                {/* Right Side - Buttons */}
                <div className="flex gap-3 flex-shrink-0">
                  <motion.a
                    href="/store"
                    className="px-5 py-2.5 bg-white text-black font-medium rounded-full text-sm lg:text-base hover:bg-gray-100 transition-all duration-300"
                    whileHover={{scale: 1.05}}
                    whileTap={{scale: 0.95}}
                  >
                    {intl.formatMessage({
                      id: 'home.productShowcaseSection.shopButton',
                    })}
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
